import type { CameraMovementClipData, MovementClipData, TimelineClip, TimelineTrack, Vector3Value } from "@/types/scene";

export function findActiveAnimationClip(tracks: TimelineTrack[], objectId: string, time: number): TimelineClip | null {
  const clips = tracks.filter((track) => track.objectId === objectId && track.trackType === "animation").flatMap((track) => track.clips);
  return clips.filter((clip) => time >= clip.startTime && time < clip.startTime + clip.duration).sort((a, b) => b.startTime - a.startTime)[0] ?? null;
}

export interface EvaluatedMovement { position: Vector3Value; rotationY: number | null; }

export function evaluateMovement(tracks: TimelineTrack[], objectId: string, time: number): EvaluatedMovement | null {
  const clips = tracks.filter((track) => track.objectId === objectId && track.trackType === "movement").flatMap((track) => track.clips).sort((a, b) => a.startTime - b.startTime);
  if (!clips.length) return null;
  const active = clips.findLast((clip) => time >= clip.startTime);
  if (!active) {
    const first = clips[0].data as unknown as MovementClipData;
    return { position: { ...first.from }, rotationY: null };
  }
  const data = active.data as unknown as MovementClipData;
  const progress = Math.min(1, Math.max(0, (time - active.startTime) / active.duration));
  const position = {
    x: data.from.x + (data.to.x - data.from.x) * progress,
    y: data.from.y + (data.to.y - data.from.y) * progress,
    z: data.from.z + (data.to.z - data.from.z) * progress,
  };
  const rotationY = data.autoRotate ? Math.atan2(data.to.x - data.from.x, data.to.z - data.from.z) : null;
  return { position, rotationY };
}

export interface EvaluatedCamera { position: Vector3Value; rotation: Vector3Value; }

export function evaluateCamera(tracks: TimelineTrack[], objectId: string, time: number): EvaluatedCamera | null {
  const clips = tracks.filter((track) => track.objectId === objectId && track.trackType === "camera").flatMap((track) => track.clips).sort((a, b) => a.startTime - b.startTime);
  if (!clips.length) return null;
  const active = clips.findLast((clip) => time >= clip.startTime);
  const data = (active ?? clips[0]).data as unknown as CameraMovementClipData;
  const progress = active ? Math.min(1, Math.max(0, (time - active.startTime) / active.duration)) : 0;
  const lerp = (from: number, to: number) => from + (to - from) * progress;
  return {
    position: { x: lerp(data.from.position.x, data.to.position.x), y: lerp(data.from.position.y, data.to.position.y), z: lerp(data.from.position.z, data.to.position.z) },
    rotation: { x: lerp(data.from.rotation.x, data.to.rotation.x), y: lerp(data.from.rotation.y, data.to.rotation.y), z: lerp(data.from.rotation.z, data.to.rotation.z) },
  };
}
